// MyRoutes.jsx
// /role?context=property  → SelectRole (property flow: 3 steps)
// /role?context=project   → SelectRole (project flow:  2 steps)
// After selection navigates to:
//   /property/create?role=owner&listtype=sale
//   /project/create?role=owner

import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext } from "react";
import { MyContext } from "../Context/MyContext";
import Auth               from "../User/Pages/Auth";
import Home               from "../User/Pages/Home";
import ResetPasswordPage  from "../User/Pages/Components/auth/ResetPasswordPage";
import ProeprtyCreate     from "../User/Pages/Components/froms/ProeprtyCreate";
import ProjectCreate      from "../User/Pages/Components/froms/ProjectCreate";
import SelectRole         from "../User/Pages/Components/froms/SelectRole";
import PropertyDetails from "../User/Pages/Components/home/Poperty/PropertyDetails";
import PropertyDetailsPage from "../User/Pages/Components/home/Poperty/PropertyDetailsPage";
import CompanyPropertiesPage from "../User/Pages/Components/home/Poperty/CompanyPropertiesPage";
import MyLayout from "../User/Layout/MyLayout";
import PropertyListing from "../User/Pages/Components/home/Poperty/PropertyListing";
import NearbyLocationsPage from "../User/Pages/NearbyLocationsPage";
import NearbyLocationRoute from "../Components/NearbyLocationRoute";
import { isNearLocationFeatureEnabled } from "../utils/nearLocationConfig";
import NearLocationConfigManagement from "../Admin/Pages/NearLocationConfigManagement";
import AboutUs from "../User/Pages/AboutUs";
import PrivacyPolicy from "../User/Pages/PrivacyPolicy";
import ContactUs from "../User/Pages/ContactUs";
import TermsConditions from "../User/Pages/TermsConditions";
import ProfilePage from "../User/Pages/Components/Profile/ProfilePage";
import MyBookings from "../User/Pages/Components/Profile/MyBookings";
import KycPage from "../User/Pages/Components/Profile/KycPage";
import ScrollToTop from "./ScrollToTop";
import NotFoundPage, { NotFoundWithSiteChrome } from "../User/Pages/NotFoundPage";
import { isUserSessionActive, isValidAuthToken } from "../utils/authSession";

// Admin Imports
import AdminLayout from "../Admin/Layout/AdminLayout";
import PartnerLayout from "../Admin/Layout/PartnerLayout";
import AdminLogin from "../Admin/Pages/AdminLogin";
import PartnerLogin from "../Admin/Pages/PartnerLogin";
import PropertyList from "../Admin/Pages/PropertyList";
import PartnerManagement from "../Admin/Pages/PartnerManagement";
import DashboardOverview from "../Admin/Pages/DashboardOverview";
import UserList from "../Admin/Pages/UserList";
import UserManagement from "../Admin/Pages/UserManagement";
import AdminList from "../Admin/Pages/AdminList";
import CreateListingWrapper from "../Admin/Pages/CreateListingWrapper";
import BookingList from "../Admin/Pages/BookingList";
import EnquiryList from "../Admin/Pages/EnquiryList";
import WebConfig from "../Admin/Pages/WebConfig";
import WebConfigEditLanguage from "../Admin/Pages/WebConfigEditLanguage";
import OwnPropertyWrapper from "../Admin/Pages/OwnPropertyWrapper";
import PartnerRequests from "../Admin/Pages/PartnerRequests";
import KycDocumentTypes from "../Admin/Pages/KycDocumentTypes";
import PartnerKycRequests from "../Admin/Pages/PartnerKycRequests";
import PartnerOwnPortfolioGate from "../Admin/Pages/PartnerOwnPortfolioGate";
import UserWebContactList from "../Admin/Pages/UserWebContactList";
import AdminPropertyLocationsMap from '../Admin/Components/AdminPropertyLocationsMap';
import NotificationManagement from "../Admin/Pages/NotificationManagement";
import LocalizationManagement from "../Admin/Pages/LocalizationManagement";
import UserKycManagement from "../Admin/Pages/UserKycManagement";
import HeroBackgroundManagement from "../Admin/Pages/HeroBackgroundManagement";
import SecuritySettings from "../Admin/Pages/SecuritySettings";
import SearchAutoSuggestions from "../Admin/Pages/SearchAutoSuggestions";
import DetailConfigPage from "../Admin/Pages/DetailConfigPage";
import PropertyListPerPageSettings from "../Admin/Pages/PropertyListPerPageSettings";
import ChatInbox from "../Admin/Pages/ChatInbox";
import ChatConfiguration from "../Admin/Pages/ChatConfiguration";
import ChatNotifications from "../Admin/Pages/ChatNotifications";
import PartnerChat from "../Admin/Pages/PartnerChat";
import PartnerUserChat from "../Admin/Pages/PartnerUserChat";
import CompanyList from "../Admin/Pages/CompanyList";
import CompanyFormPage from "../Admin/Pages/CompanyFormPage";


const RequireAuth = ({ children }) => {
  const { user } = useContext(MyContext);
  const location = useLocation();
  const isAuthenticated =
    isUserSessionActive() || isValidAuthToken(user?.access_token);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const LegacyNearbyRedirect = () => {
  const { nearLocationConfig } = useContext(MyContext);
  const to = isNearLocationFeatureEnabled(nearLocationConfig) ? '/nearby-locations' : '/';
  return <Navigate to={to} replace />;
};

const MyRoutes = () => {
  return (
   <>
   <ScrollToTop />
    <Routes>
      <Route path="/login"               element={<Auth />} />
      <Route path="/" element={<MyLayout />}>
        <Route index element={<Home />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Selection flow — context via query param */}
       <Route path="/role"            element={<RequireAuth><SelectRole /></RequireAuth>} />

        {/* Create screens — role & listtype via query params */}
     <Route path="/property/create" element={<RequireAuth><ProeprtyCreate /></RequireAuth>} />
        <Route path="/property/:id/:slug?"    element={<PropertyDetailsPage />} />
        <Route path="/view/property/:id/:slug?" element={<PropertyDetailsPage />} />
        <Route path="/company/:id" element={<CompanyPropertiesPage />} />
       <Route path="/project/create"  element={<RequireAuth><ProjectCreate /></RequireAuth>} />
        <Route path="/properties" element={<PropertyListing />} />
        <Route path="/nearby-locations" element={<NearbyLocationRoute><NearbyLocationsPage /></NearbyLocationRoute>} />
        <Route path="/new-listings" element={<LegacyNearbyRedirect />} />
        
        <Route path="about" element={<AboutUs />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/account" element={<ProfilePage />} />
        <Route path="/profile" element={<Navigate to="/account" replace />} />
        <Route path="/my-bookings" element={<MyBookings />} />
        <Route path="/kyc" element={<KycPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<DashboardOverview />} />
        <Route path="properties" element={<PropertyList />} />
        <Route path="my-listings" element={<PropertyList isMine={true} />} />
        <Route path="my-properties" element={<PropertyList isOwn={true} />} />
        <Route path="awaiting-listings" element={<PropertyList isAwaiting={true} />} />
        <Route path="sold-out-properties" element={<PropertyList isSoldOut={true} />} />
        <Route path="create-listing" element={<CreateListingWrapper />} />
      <Route 
  path="own-property" 
  element={<OwnPropertyWrapper key="property" forcedType="property" />} 
/>


<Route
  path="property-locations-map"
  element={<AdminPropertyLocationsMap standalone={true} />}
/>

<Route 
  path="own-project" 
  element={<OwnPropertyWrapper key="project" forcedType="project" />} 
/>
        <Route path="partners" element={<PartnerManagement />} />
        <Route path="companies" element={<CompanyList />} />
        <Route path="companies/new" element={<CompanyFormPage />} />
        <Route path="companies/:id/edit" element={<CompanyFormPage />} />
        <Route path="users" element={<UserList />} />
        <Route path="user-management" element={<UserManagement />} />
        <Route path="admins" element={<AdminList />} />
        <Route path="config" element={<WebConfig />} />
        <Route path="property-detail-config" element={<DetailConfigPage mode="property" />} />
        <Route path="project-detail-config" element={<DetailConfigPage mode="project" />} />
        <Route path="config-language" element={<WebConfigEditLanguage />} />
        <Route path="bookings" element={<BookingList />} />
        <Route path="enquiries" element={<EnquiryList />} />
        <Route path="user-web-contacts" element={<UserWebContactList />} />
        <Route path="partner-requests" element={<PartnerRequests />} />
        <Route path="kyc-document-types" element={<KycDocumentTypes />} />
        <Route path="user-kyc" element={<UserKycManagement />} />
        <Route path="hero-backgrounds" element={<HeroBackgroundManagement />} />
        <Route path="near-location-config" element={<NearLocationConfigManagement />} />
        <Route path="partner-kyc-requests" element={<PartnerKycRequests />} />
        <Route path="notification-management" element={<NotificationManagement />} />
        <Route path="localization" element={<LocalizationManagement />} />
        <Route path="search-auto-suggestions" element={<SearchAutoSuggestions />} />
        <Route path="property-list-per-page" element={<PropertyListPerPageSettings />} />
        <Route path="security-settings" element={<SecuritySettings />} />
        <Route path="chat" element={<ChatInbox />} />
        <Route path="unknown-user" element={<ChatInbox />} />
        <Route path="chat-notifications" element={<ChatNotifications />} />
        <Route path="chat-configuration" element={<ChatConfiguration />} />
        <Route path="partner-chat" element={<PartnerChat />} />
      </Route>

      {/* Partner Routes — Separate namespace, same components */}
      <Route path="/partner/login" element={<PartnerLogin />} />
      <Route path="/partner" element={<PartnerLayout />}>
        <Route path="dashboard" element={<DashboardOverview />} />
        <Route path="properties" element={<PropertyList />} />
        <Route path="my-listings" element={<PropertyList isMine={true} />} />
        <Route path="awaiting-listings" element={<PropertyList isAwaiting={true} />} />
        <Route path="sold-out-properties" element={<PropertyList isSoldOut={true} />} />
        <Route path="create-listing" element={<CreateListingWrapper />} />
        <Route element={<PartnerOwnPortfolioGate />}>
          <Route path="my-properties" element={<PropertyList isOwn={true} />} />
          <Route path="own-property"   element={<OwnPropertyWrapper forcedType="property" />} />
          <Route path="own-project"    element={<OwnPropertyWrapper forcedType="project" />} />
        </Route>
        <Route path="bookings" element={<BookingList />} />
        <Route path="enquiries" element={<EnquiryList />} />
        <Route path="user-web-contacts" element={<UserWebContactList />} />
        <Route path="partner-requests" element={<PartnerRequests />} />
         <Route path="property-locations-map" element={<AdminPropertyLocationsMap standalone={true} />} />
        <Route path="chat" element={<PartnerChat />} />
        <Route path="user-chat" element={<PartnerUserChat />} />
        <Route path="chat-notifications" element={<ChatNotifications />} />

      </Route>

      <Route path="*" element={<NotFoundWithSiteChrome />} />
    </Routes>
   </>
  );
};

export default MyRoutes;